// ============================================================
// SectionBlock
// ============================================================
// MOBILE change:
//   The bar grid previously set gridTemplateColumns via an
//   inline style, which CSS media queries cannot override.
//   It now writes the column count as a CSS custom property
//   (--section-cols) on the element, and a real CSS class
//   "bars-grid" provides the grid layout. mobile.css targets
//   .bars-grid to change column count at each breakpoint.
//   The gap is also switched to the CSS class approach.
// ============================================================

import React, { useState, useRef, useEffect, useCallback } from 'react';
import type { Section } from '../../core/types';
import { useSongStore, effectiveKey } from '../../store/songStore';
import { formatKey } from '../../core/formatter';
import { SlashBar } from './SlashBar';
import { AnnotationField } from './AnnotationField';
import { KeyDetectButton } from '../editor/KeyDetectButton';
import { extractChords, detectModulation } from '../../core/keyDetection';
import { MAJOR_KEYS, MINOR_KEYS } from '../../core/transposer';

interface SectionBlockProps {
  section: Section;
  sectionIndex: number;
  selectedBarId: string | null;
  onSelectBar: (barId: string) => void;
  onNavigateBar: (
    sectionId: string,
    barId: string,
    direction: 'prev' | 'next' | 'up' | 'down' | 'nextSection',
  ) => void;
}

export function SectionBlock({
  section,
  sectionIndex,
  selectedBarId,
  onSelectBar,
  onNavigateBar,
}: SectionBlockProps) {
  const song                  = useSongStore((s) => s.song);
  const updateSectionTitle    = useSongStore((s) => s.updateSectionTitle);
  const deleteSection         = useSongStore((s) => s.deleteSection);
  const addBar                = useSongStore((s) => s.addBar);
  const deleteBar             = useSongStore((s) => s.deleteBar);
  const setSectionKeyOverride = useSongStore((s) => s.setSectionKeyOverride);
  const moveSectionUp         = useSongStore((s) => s.moveSectionUp);
  const moveSectionDown       = useSongStore((s) => s.moveSectionDown);
  const duplicateSection      = useSongStore((s) => s.duplicateSection);
  const setRepeat             = useSongStore((s) => s.setRepeat);
  const removeRepeat          = useSongStore((s) => s.removeRepeat);
  const setSectionBarsPerRow  = useSongStore((s) => s.setSectionBarsPerRow);
  const setSectionNote        = useSongStore((s) => s.setSectionNote);

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue]         = useState(section.title);
  const [showKeyPicker, setShowKeyPicker]   = useState(false);
  const [repeatRangeStart, setRepeatRangeStart] = useState<string | null>(null);

  const titleInputRef = useRef<HTMLInputElement>(null);
  const keyPickerRef  = useRef<HTMLDivElement>(null);

  const sectionKey     = effectiveKey(song, section);
  const hasKeyOverride = section.key !== null;
  const barsPerRow     = section.barsPerRow ?? song.barsPerRow ?? 4;

  const modulationHint = (song.analysisSettings?.enabled && !hasKeyOverride)
    ? detectModulation(section.bars, song.defaultKey)
    : null;
  const showModulationHint = modulationHint?.isModulation ?? false;
  void showModulationHint;

  useEffect(() => { setTitleValue(section.title); }, [section.title]);

  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  useEffect(() => {
    if (!showKeyPicker) return;
    const handle = (e: MouseEvent) => {
      if (keyPickerRef.current && !keyPickerRef.current.contains(e.target as Node)) setShowKeyPicker(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [showKeyPicker]);

  const commitTitle = useCallback(() => {
    const trimmed = titleValue.trim();
    if (trimmed && trimmed !== section.title) updateSectionTitle(section.id, trimmed);
    else setTitleValue(section.title);
    setIsEditingTitle(false);
  }, [titleValue, section.id, section.title, updateSectionTitle]);

  const handleTitleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') commitTitle();
    if (e.key === 'Escape') { setTitleValue(section.title); setIsEditingTitle(false); }
  }, [commitTitle, section.title]);

  const handleShiftClick = useCallback((barId: string) => {
    if (repeatRangeStart === null) {
      setRepeatRangeStart(barId);
    } else {
      if (repeatRangeStart === barId) { setRepeatRangeStart(null); return; }
      const bars = section.bars;
      const startIdx = bars.findIndex((b) => b.id === repeatRangeStart);
      const endIdx   = bars.findIndex((b) => b.id === barId);
      if (startIdx < 0 || endIdx < 0) { setRepeatRangeStart(null); return; }
      const [resolvedStart, resolvedEnd] = startIdx <= endIdx
        ? [repeatRangeStart, barId]
        : [barId, repeatRangeStart];
      setRepeat(section.id, resolvedStart, resolvedEnd, 2);
      setRepeatRangeStart(null);
    }
  }, [repeatRangeStart, section.bars, section.id, setRepeat]);

  const cancelRepeatSelection = useCallback(() => setRepeatRangeStart(null), []);

  const getRangeSelectedBars = useCallback((): Set<string> => {
    if (repeatRangeStart === null) return new Set();
    return new Set([repeatRangeStart]);
  }, [repeatRangeStart]);

  const rangeSelectedBars = getRangeSelectedBars();

  const handleNavigate = useCallback(
    (barId: string, direction: 'prev' | 'next' | 'up' | 'down' | 'nextSection') =>
      onNavigateBar(section.id, barId, direction),
    [section.id, onNavigateBar],
  );

  const handleAddBar    = useCallback(() => addBar(section.id), [section.id, addBar]);
  const handleDeleteBar = useCallback((barId: string) => deleteBar(section.id, barId), [section.id, deleteBar]);

  return (
    <div className="section-block mb-10 group/section" data-section-id={section.id}>

      {/* Section header */}
      {/* MOBILE: section-header class — mobile.css adjusts padding/font on phone */}
      <div className="section-header flex items-center gap-3 mb-3">

        {isEditingTitle ? (
          <input
            ref={titleInputRef} type="text" value={titleValue}
            onChange={(e) => setTitleValue(e.target.value)}
            onBlur={commitTitle} onKeyDown={handleTitleKeyDown}
            className="section-title-input bg-transparent border-b border-accent outline-none text-sm font-label tracking-widest uppercase text-ink-secondary w-40"
          />
        ) : (
          <button onClick={() => setIsEditingTitle(true)} className="section-title text-sm font-label tracking-widest uppercase text-ink-secondary hover:text-ink-primary transition-colors cursor-text">
            {section.title}
          </button>
        )}

        {/* Key badge */}
        <div className="relative" ref={keyPickerRef}>
          <button
            onClick={() => setShowKeyPicker((v) => !v)}
            className={[
              'key-badge text-[11px] font-mono px-2 py-0.5 rounded-full border transition-colors',
              hasKeyOverride
                ? 'border-accent text-accent bg-accent/10 hover:bg-accent/20'
                : 'border-rule text-ink-muted hover:border-ink-muted hover:text-ink-secondary',
            ].join(' ')}
            title={hasKeyOverride ? 'Section key override — click to change' : 'Click to set section key'}
          >
            {hasKeyOverride ? formatKey(sectionKey) : `↳ ${formatKey(song.defaultKey)}`}
          </button>

          {showKeyPicker && (
            <div className="absolute top-full left-0 mt-1 bg-paper border border-rule rounded shadow-lg p-3 w-64" style={{ zIndex: 200 }}>
              <div className="text-[10px] font-label tracking-widest uppercase text-ink-muted mb-2">Major</div>
              <div className="grid grid-cols-6 gap-1 mb-3">
                {MAJOR_KEYS.map((k) => (
                  <button key={k.root} onClick={() => { setSectionKeyOverride(section.id, k); setShowKeyPicker(false); }}
                    className={['text-xs py-1 rounded border transition-colors', section.key?.root === k.root && section.key?.scale === k.scale ? 'border-accent text-accent bg-accent/10' : 'border-rule text-ink-secondary hover:border-ink-secondary'].join(' ')}>
                    {k.root}
                  </button>
                ))}
              </div>
              <div className="text-[10px] font-label tracking-widest uppercase text-ink-muted mb-2">Minor</div>
              <div className="grid grid-cols-6 gap-1 mb-3">
                {MINOR_KEYS.map((k) => (
                  <button key={k.root} onClick={() => { setSectionKeyOverride(section.id, k); setShowKeyPicker(false); }}
                    className={['text-xs py-1 rounded border transition-colors', section.key?.root === k.root && section.key?.scale === k.scale ? 'border-accent text-accent bg-accent/10' : 'border-rule text-ink-secondary hover:border-ink-secondary'].join(' ')}>
                    {k.root}m
                  </button>
                ))}
              </div>
              {hasKeyOverride && (
                <button onClick={() => { setSectionKeyOverride(section.id, null); setShowKeyPicker(false); }} className="text-[10px] text-ink-muted hover:text-error transition-colors w-full text-left mt-1">
                  ✕ Remove key override
                </button>
              )}
            </div>
          )}
        </div>

        {/* Bars per row toggle */}
        <div className="mode-toggle">
          {[2, 3, 4, 8].map((n) => (
            <button key={n} className={`mode-toggle-btn ${barsPerRow === n ? 'active' : ''}`}
              onClick={() => setSectionBarsPerRow(section.id, n === song.barsPerRow ? null : n)}
              title={`${n} bars per row`}>
              {n}
            </button>
          ))}
        </div>

        <div className="flex-1" />

        {/* Section controls */}
        <div className="section-controls flex items-center gap-1 opacity-0 group-hover/section:opacity-100 transition-opacity">
          <button onClick={() => moveSectionUp(section.id)}    className="control-btn" title="Move up"     disabled={sectionIndex === 0}>↑</button>
          <button onClick={() => moveSectionDown(section.id)}  className="control-btn" title="Move down">↓</button>
          <button onClick={() => duplicateSection(section.id)} className="control-btn" title="Duplicate">⎘</button>
          <button onClick={() => deleteSection(section.id)}    className="control-btn text-error hover:bg-error/10" title="Delete">✕</button>
        </div>
      </div>

      {/* Section annotation */}
      <div style={{ marginBottom: section.note ? 6 : 0, marginTop: section.note ? -4 : 0 }}>
        <AnnotationField value={section.note ?? null} onChange={(note) => setSectionNote(section.id, note)} placeholder="Add section note…" showHint={false} />
      </div>

      {/* Repeat selection hint */}
      {repeatRangeStart !== null && (
        <div className="flex items-center gap-3 mb-2 px-3 py-1.5 bg-accent/10 border border-accent/30 rounded text-[11px] font-mono text-accent">
          <span>Shift-click a second bar to close the repeat</span>
          <button onClick={cancelRepeatSelection} className="ml-auto text-accent/60 hover:text-accent">Cancel</button>
        </div>
      )}

      {/*
        MOBILE: Bar grid
        Previously: style={{ display: 'grid', gridTemplateColumns: `repeat(${barsPerRow}, 1fr)`, ... }}
        Now: "bars-grid" CSS class handles the layout. The desktop column count is set
        via the --bars-per-row CSS custom property so the user's chosen barsPerRow
        still works, while mobile.css can override the column count at breakpoints.

        The inline style now only sets the custom property — no gridTemplateColumns inline.
        mobile.css targets .bars-grid and overrides the columns at ≤767px and 768-1023px.
      */}
      <div
        className="bars-grid"
        style={{
          // Pass the user-chosen column count as a CSS var.
          // The .bars-grid rule in index.css (or below) reads it.
          // mobile.css overrides grid-template-columns at breakpoints.
          ['--bars-per-row' as string]: barsPerRow,
          border: '1px solid var(--color-rule)',
        }}
      >
        {section.bars.map((bar, barIndex) => (
          <SlashBar
            key={bar.id}
            bar={bar}
            section={section}
            sectionIndex={sectionIndex}
            barIndex={barIndex}
            isSelected={selectedBarId === bar.id}
            isRangeSelected={rangeSelectedBars.has(bar.id)}
            onSelect={() => onSelectBar(bar.id)}
            onShiftClick={() => handleShiftClick(bar.id)}
            onNavigate={(dir) => handleNavigate(bar.id, dir)}
            onDeleteBar={() => handleDeleteBar(bar.id)}
          />
        ))}

        <button
          onClick={handleAddBar}
          className="add-bar-cell min-h-[72px] flex items-center justify-center border border-dashed border-rule text-ink-muted hover:text-ink-secondary hover:border-ink-muted transition-colors text-xl"
          title="Add bar"
        >
          +
        </button>
      </div>
    </div>
  );
}